/**
 * Flow editor — reusable floating window chrome.
 *
 * Wraps any panel (Audio Sync Debug, Pattern Library, Pattern Difficulty, …)
 * with a draggable title bar, a resize grip, and a close button, and persists
 * its position + size to localStorage per `id` so it reopens where the user
 * left it.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { color } from '../../components/ui/theme';
import { IcClose } from '../../components/ui/icons';

export interface WinRect { x: number; y: number; w: number; h: number; }

const KEY = (id: string) => `synapse:flow:win:${id}`;

function loadRect(id: string): Partial<WinRect> | null {
  try {
    const raw = localStorage.getItem(KEY(id));
    return raw ? (JSON.parse(raw) as Partial<WinRect>) : null;
  } catch { return null; }
}
function saveRect(id: string, r: WinRect): void {
  try { localStorage.setItem(KEY(id), JSON.stringify(r)); } catch { /* quota */ }
}

/** Clamp a rect so the title bar stays grabbable inside the viewport. */
function clampRect(r: WinRect): WinRect {
  const vw = window.innerWidth, vh = window.innerHeight;
  const w = Math.min(Math.max(160, r.w), Math.max(160, vw - 8));
  const h = Math.min(Math.max(100, r.h), Math.max(100, vh - 8));
  const x = Math.min(Math.max(0, r.x), Math.max(0, vw - 60));
  const y = Math.min(Math.max(0, r.y), Math.max(0, vh - 32));
  return { x, y, w, h };
}

export default function FloatingWindow({
  id,
  title,
  onClose,
  defaultRect,
  minW = 180,
  minH = 110,
  resizable = true,
  accent = color.accentSoft,
  children,
}: {
  id: string;
  title: string;
  onClose: () => void;
  defaultRect: WinRect;
  minW?: number;
  minH?: number;
  resizable?: boolean;
  accent?: string;
  children: React.ReactNode;
}) {
  const [rect, setRect] = useState<WinRect>(() =>
    clampRect({ ...defaultRect, ...(loadRect(id) || {}) }),
  );
  const rectRef = useRef(rect);
  rectRef.current = rect;
  const [closeHover, setCloseHover] = useState(false);

  // Re-clamp if the window is resized smaller than the panel's saved position.
  useEffect(() => {
    const onResize = () => setRect(r => clampRect(r));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const beginDrag = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY;
    const start = rectRef.current;
    // Track the latest rect locally so the saved value never lags React's
    // (batched) state commits — important for fast drags.
    let latest = start;
    const onMove = (ev: MouseEvent) => {
      latest = clampRect({ ...start, x: start.x + (ev.clientX - sx), y: start.y + (ev.clientY - sy) });
      setRect(latest);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      saveRect(id, latest);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [id]);

  const beginResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX, sy = e.clientY;
    const start = rectRef.current;
    let latest = start;
    const onMove = (ev: MouseEvent) => {
      latest = clampRect({
        ...start,
        w: Math.max(minW, start.w + (ev.clientX - sx)),
        h: Math.max(minH, start.h + (ev.clientY - sy)),
      });
      setRect(latest);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      saveRect(id, latest);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [id, minW, minH]);

  return (
    <div style={{ ...frameStyle, left: rect.x, top: rect.y, width: rect.w, height: rect.h }}>
      <div onMouseDown={beginDrag} style={{ ...headerStyle, borderBottom: '1px solid var(--line)' }}>
        <span style={{ ...titleStyle, color: accent }}>{title}</span>
        <button
          type="button"
          title="Close"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          onMouseEnter={() => setCloseHover(true)}
          onMouseLeave={() => setCloseHover(false)}
          style={{
            ...closeBtnStyle,
            ...(closeHover
              ? { background: 'var(--accent)', borderColor: 'var(--accent)', color: 'oklch(0.14 calc(0.022 * var(--tint-c)) var(--tint-h))' }
              : null),
          }}
        >
          <IcClose size={14} />
        </button>
      </div>
      <div style={contentStyle}>{children}</div>
      {resizable && <div onMouseDown={beginResize} style={resizeGripStyle} title="Resize" />}
    </div>
  );
}

// Floating draggable panel with the same minimalist glass look as the
// "Open Beatmap" dialog: big rounded corners, blurred translucent surface,
// soft shadow + faint accent ring. Stays `position: fixed` with a lower
// z-index so it sits under true modals; `overflow: hidden` clips the grip.
const frameStyle: React.CSSProperties = {
  position: 'fixed',
  zIndex: 40,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 18,
  background: 'oklch(0.10 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.86)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.28)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 24px 70px rgba(0, 0, 0, 0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.12)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '10px 12px',
  cursor: 'move',
  userSelect: 'none',
  flex: '0 0 auto',
};

const titleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
};

const closeBtnStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: 'transparent',
  // Accent ring + accent X at rest; hover fills with accent (X turns dark tint).
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.55)',
  color: 'var(--accent)',
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  flexShrink: 0,
  transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: '10px 12px',
};

// Bottom-right resize grip — two diagonal hint lines.
const resizeGripStyle: React.CSSProperties = {
  position: 'absolute',
  right: 0,
  bottom: 0,
  width: 16,
  height: 16,
  cursor: 'nwse-resize',
  background:
    'linear-gradient(135deg, transparent 0 50%, rgba(167,139,250,0.5) 50% 60%, transparent 60% 70%, rgba(167,139,250,0.5) 70% 80%, transparent 80%)',
};
